import { useEffect } from 'react'
import { isIOSDevice, isMobileDevice } from '@/controller/pwaInstall.ts'

const MANIFEST_LINK_ID = 'dynamic-web-app-manifest'
const FALLBACK_ICON_URL = '/favicon.png'
const ICON_SIZES = [192, 512]

// Chromium wants a 192px and a 512px icon; a smaller thumbnail would cost
// installability, so below that the bundled icon is the better answer.
const MIN_REMOTE_ICON_SIZE = 192

interface ManifestIcon {
  src: string
  sizes: string
  type?: string
  purpose: string
}

export interface WebAppManifestOptions {
  /** App name on the home screen — the drive title. */
  name: string
  /** Absolute URL the installed app opens; carries the auth token. */
  startUrl: string
  /** Remote image used as app icon (drive/album thumbnail). */
  iconUrl: string | null
  themeColor?: string | null
  backgroundColor?: string | null
}

function loadImage(url: string, useCors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    // Only with CORS does the canvas stay untainted and exportable. Hosts that
    // send no CORS header fail the load outright, hence the second, plain try.
    if (useCors) {
      image.crossOrigin = 'anonymous'
    }
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = url
  })
}

/** Cover-crops the image into a square PNG data URL of exactly `size` pixels. */
function renderSquareIcon(image: HTMLImageElement, size: number): string {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) throw new Error('no canvas context')

  const shorterSide = Math.min(image.naturalWidth, image.naturalHeight)
  const offsetX = (image.naturalWidth - shorterSide) / 2
  const offsetY = (image.naturalHeight - shorterSide) / 2
  context.drawImage(image, offsetX, offsetY, shorterSide, shorterSide, 0, 0, size, size)

  // Throws for a tainted canvas (thumbnail host without CORS) — the caller then
  // falls back to the bundled icon.
  return canvas.toDataURL('image/png')
}

/**
 * Best case: exact square icons in the sizes Chromium asks for, rendered by us
 * and embedded as data URLs. Needs a CORS header on the image host.
 */
async function renderIcons(url: string): Promise<ManifestIcon[] | null> {
  try {
    const image = await loadImage(url, true)
    const icons: ManifestIcon[] = ICON_SIZES.map(size => ({
      src: renderSquareIcon(image, size),
      sizes: `${size}x${size}`,
      type: 'image/png',
      purpose: 'any',
    }))
    // Same bitmap as maskable so Android fills the icon shape instead of
    // shrinking the photo onto a white tile.
    icons.push({ ...icons[icons.length - 1], purpose: 'maskable' })
    return icons
  } catch {
    return null
  }
}

/**
 * Fallback for hosts without CORS: hand the URL to the browser as-is. It fetches
 * manifest icons itself, no CORS involved — we only have to make sure the image
 * is big enough for the sizes we declare.
 */
async function buildRemoteIcon(url: string): Promise<ManifestIcon[] | null> {
  try {
    const image = await loadImage(url, false)
    const shorterSide = Math.min(image.naturalWidth, image.naturalHeight)
    if (shorterSide < MIN_REMOTE_ICON_SIZE) return null
    // One source declared for both required sizes — the browser scales.
    return [{ src: url, sizes: ICON_SIZES.map(size => `${size}x${size}`).join(' '), purpose: 'any' }]
  } catch {
    return null
  }
}

async function buildIcons(iconUrl: string | null): Promise<ManifestIcon[]> {
  if (iconUrl) {
    const rendered = await renderIcons(iconUrl)
    if (rendered) return rendered

    const remote = await buildRemoteIcon(iconUrl)
    if (remote) {
      // Not fatal, but the rendered variant is squared and maskable — worth a
      // CORS header on the thumbnail host.
      console.warn('[manifest] thumbnail not readable via CORS, using it unprocessed as app icon')
      return remote
    }
  }

  return (await renderIcons(FALLBACK_ICON_URL)) ?? []
}

/**
 * Injects a per-drive web app manifest so Chromium can offer a native install
 * (see `pwaInstall.ts`). Built at runtime as a blob URL because every drive is
 * its own app: own name, own icon, own start URL.
 *
 * Deliberately not applied on iOS: there the home screen entry is created from
 * the currently open URL plus the per-drive `apple-touch-icon`
 * (`GenericDrive.page.tsx`), and a manifest would override exactly that.
 *
 * Not on desktop either — without a manifest Chrome offers no install at all,
 * which is what we want: galleries are installed on phones only.
 */
export function useWebAppManifest(options: WebAppManifestOptions | null) {
  const name = options?.name ?? null
  const startUrl = options?.startUrl ?? null
  const iconUrl = options?.iconUrl ?? null
  const themeColor = options?.themeColor ?? null
  const backgroundColor = options?.backgroundColor ?? null

  useEffect(() => {
    if (!name || !startUrl) return
    if (isIOSDevice() || !isMobileDevice()) return

    let isCancelled = false
    let blobUrl: string | null = null
    let link: HTMLLinkElement | null = null

    void (async () => {
      const icons = await buildIcons(iconUrl)
      // Without icons the manifest would not be installable anyway.
      if (isCancelled || icons.length === 0) return

      const manifest = {
        name,
        short_name: name,
        start_url: startUrl,
        // Absolute URLs throughout: relative ones cannot be resolved against a
        // blob: manifest URL.
        scope: `${window.location.origin}/`,
        display: 'standalone',
        background_color: backgroundColor ?? '#FFFFFF',
        theme_color: themeColor ?? '#FFFFFF',
        icons,
      }

      blobUrl = URL.createObjectURL(
        new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
      )

      link = document.getElementById(MANIFEST_LINK_ID) as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.id = MANIFEST_LINK_ID
        link.rel = 'manifest'
        document.head.appendChild(link)
      }
      link.href = blobUrl
    })()

    return () => {
      isCancelled = true
      link?.remove()
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [name, startUrl, iconUrl, themeColor, backgroundColor])
}
